import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Target, AlertTriangle } from 'lucide-react';
interface SlideExecutionRationaleProps {
  onContinue: () => void;
}
const IMPACT_FACTORS = ['How the content is shortened', 'How the interaction is structured', 'How campaigns are assembled', 'How localization is handled', 'How engagement data is interpreted', 'How learning is applied to the next execution'];
const POINTS = [{
  num: '01',
  text: 'Our platform is judged on impact — not on assets delivered.'
}, {
  num: '02',
  text: 'Impact depends on how campaigns are adapted, deployed and iterated, not on how they were made.'
}, {
  num: '03',
  text: 'Self-service execution inside the platform breaks that loop and degrades outcomes.'
}];
export function SlideExecutionRationale({
  onContinue
}: SlideExecutionRationaleProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const timers = [300, 800, 1600, 2400, 3200].map((ms, idx) => setTimeout(() => setStage(idx + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-12 max-w-7xl mx-auto bg-slate-50" data-id="element-572" data-mp="ccdibo14jhmk">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-3 shrink-0" data-id="element-573" data-mp="h7j3gkmpcs9l">
        <p className="text-[#6C2BD9] font-semibold tracking-widest uppercase text-sm" data-id="element-574" data-mp="3llrc5sjcwfnb">
          Why execution stays with us
        </p>
        <h2 className="text-3xl md:text-5xl font-serif text-slate-900" data-id="element-575" data-mp="3ujthule1bwgx">
          Not because you can't. Because outcomes are better.
        </h2>
        <p className="text-lg text-slate-500" data-id="element-576" data-mp="3bp34pzzvb83">
          The argument is not capability. It is where the specialist execution
          and optimization loop should live.
        </p>
      </motion.div>

      <div className="flex-1 min-h-0 flex flex-col justify-center max-w-5xl mx-auto w-full mt-8 space-y-6" data-id="element-577" data-mp="1ev1keiqip0oo">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: stage >= 1 ? 1 : 0,
        y: stage >= 1 ? 0 : 16
      }} className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm" data-id="element-578" data-mp="p6mh84at3p8d">
          <div className="flex items-center gap-4 mb-6" data-id="element-579" data-mp="6ebh2i7je5dy">
            <div className="w-10 h-10 rounded-xl bg-[#6C2BD9]/10 text-[#6C2BD9] flex items-center justify-center shrink-0" data-id="element-580" data-mp="6etl410fxnwf">
              <Target className="w-5 h-5" data-id="element-581" data-mp="1d4bmpsv9iag" />
            </div>
            <h3 className="text-slate-900 font-bold text-xl" data-id="element-582" data-mp="g4womv56uk90">
              Campaign impact depends on
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mb-6" data-id="element-583" data-mp="bbboawg4he6n">
            {IMPACT_FACTORS.map((factor, idx) => <motion.div key={factor} initial={{
            opacity: 0,
            x: -8
          }} animate={{
            opacity: stage >= 2 ? 1 : 0,
            x: stage >= 2 ? 0 : -8
          }} transition={{
            delay: stage >= 2 ? idx * 0.05 : 0
          }} className="flex items-center gap-3" data-id="element-584" data-mp="3eun2ei2lj3ej" data-mpi={idx}>
                <div className="w-1.5 h-1.5 rounded-full bg-[#6C2BD9] shrink-0" data-id="element-585" data-mp="1pax0j42ebixm" />
                <span className="text-slate-700 text-sm" data-id="element-586" data-mp="2d3dh1a6hj2ut">{factor}</span>
              </motion.div>)}
          </div>

          <div className="pt-5 border-t border-slate-100" data-id="element-587" data-mp="2hzav19u9zx4f">
            <p className="text-slate-500 text-sm" data-id="element-588" data-mp="241ckygmzmmuq">
              Every one of those is a craft decision, made hundreds of times a
              year, and it only gets better if the same team keeps making it.
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: stage >= 3 ? 1 : 0,
        y: stage >= 3 ? 0 : 16
      }} className="bg-rose-50 border border-rose-200 rounded-2xl p-6 flex items-start gap-4" data-id="element-589" data-mp="wou0cltq6uv4">
          <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-500 flex items-center justify-center shrink-0" data-id="element-590" data-mp="134vx1wg5u2sa">
            <AlertTriangle className="w-5 h-5" data-id="element-591" data-mp="y7vmb88d8buu" />
          </div>
          <p className="text-slate-800 leading-relaxed pt-1" data-id="element-592" data-mp="1vuuotzlqoe0n">
            <span className="text-rose-500 font-semibold" data-id="element-593" data-mp="1vs8ghxr0xuhd">
              The self-service failure mode:
            </span>{' '}
            a local team ships the campaign once, never returns to the
            optimization loop, and performance quietly degrades with nobody
            accountable for it. Output goes up. Impact goes down.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: stage >= 4 ? 1 : 0,
        y: stage >= 4 ? 0 : 16
      }} className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm" data-id="element-594" data-mp="aijhslnzgryv">
          <p className="text-slate-400 font-bold tracking-widest uppercase text-xs mb-6" data-id="element-595" data-mp="2m4v6ss6erz31">
            Why execution stays with Snakker
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-id="element-596" data-mp="2vtv7qh4h14jv">
            {POINTS.map((point, idx) => <motion.div key={point.num} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: stage >= 5 ? 1 : 0,
            y: stage >= 5 ? 0 : 12
          }} transition={{
            delay: stage >= 5 ? idx * 0.1 : 0
          }} className="flex items-start gap-3" data-id="element-597" data-mp="30u0zryenkt2h" data-mpi={idx}>
                <span className="text-[#6C2BD9] font-bold text-lg leading-snug" data-id="element-598" data-mp="1kezr4pm9kt0o">
                  {point.num}
                </span>
                <p className="text-slate-700 text-sm leading-relaxed" data-id="element-599" data-mp="2np3vnnt4c5ea">
                  {point.text}
                </p>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: stage >= 5 ? 1 : 0
        }} className="pt-6 border-t border-slate-100" data-id="element-600" data-mp="2wqbt63nt927b">
            <p className="text-slate-800 text-lg leading-relaxed" data-id="element-601" data-mp="36nggi6te73pp">
              It is not that LEO could not run it.{' '}
              <span className="text-[#6C2BD9] font-semibold" data-id="element-602" data-mp="31penou65zq11">
                Outcomes are simply better when the expert execution loop stays
                with Snakker.
              </span>{' '}
              LEO keeps the steering wheel; we keep the engine running.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>;
}